import {useMemo, useState} from 'react';
import {
  Box,
  Button,
  Flex,
  FormControl,
  FormErrorMessage,
  FormLabel,
  HStack,
  Input,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalOverlay,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Select,
  Text,
  VStack,
  useToast,
} from '@chakra-ui/react';
import {
  TablerBuilding,
  TablerCheck,
  TablerChevronDown,
  TablerMapPin,
} from '../../icons';
import LanguageMultiSelect from '../common/LanguageMultiSelect';
import usePlannerStyles from '../planner/usePlannerStyles';
import {
  combinePhoneExtension,
  splitPhoneExtension,
} from '../../utils/phoneExtension';
import type {ProviderProfileInput} from '../../services/userProfileService';
import type {OnboardingFacility} from '../../services/onboardingService';
import type {UserData} from '../../types';

// Professional titles offered up front. "Other" reveals a free-text field so
// we don't force an incomplete list on the provider.
export const TITLE_OPTIONS = [
  'Dr.',
  'MD',
  'DO',
  'NP',
  'PA',
  'RN',
  'LPN',
  'CNM',
  'CRNA',
  'PharmD',
  'CMA',
  'RMA',
  'CCMA',
  'PSC',
  'Scheduler',
  'RD',
  'PhD',
];
export const OTHER_TITLE = 'Other';

interface OnboardingModalProps {
  isOpen: boolean;
  userData: UserData;
  facilities?: OnboardingFacility[];
  facilityRequired?: boolean;
  onComplete: (
    profile: ProviderProfileInput,
    facilityId?: string,
  ) => Promise<void>;
}

export default function OnboardingModal({
  isOpen,
  userData,
  facilities = [],
  facilityRequired = false,
  onComplete,
}: OnboardingModalProps) {
  const styles = usePlannerStyles();
  const toast = useToast();

  const initialTitle = userData.title || '';
  const initialTitleIsPreset = TITLE_OPTIONS.includes(initialTitle);

  const [firstName, setFirstName] = useState(userData.firstName || '');
  const [lastName, setLastName] = useState(userData.lastName || '');
  const [titleSelection, setTitleSelection] = useState(
    initialTitle ? (initialTitleIsPreset ? initialTitle : OTHER_TITLE) : '',
  );
  const [customTitle, setCustomTitle] = useState(
    initialTitle && !initialTitleIsPreset ? initialTitle : '',
  );
  // A soft nudge: providers explicitly skip the (optional) title rather than
  // silently ignoring it. Skipping just hides the picker; submit proceeds with
  // an empty title either way.
  const [titleSkipped, setTitleSkipped] = useState(false);
  const [phone, setPhone] = useState(
    splitPhoneExtension(userData.providerPhone || '').number,
  );
  const [phoneExt, setPhoneExt] = useState(
    splitPhoneExtension(userData.providerPhone || '').extension,
  );
  const [spokenLanguages, setSpokenLanguages] = useState<string[]>(
    userData.spokenLanguages && userData.spokenLanguages.length > 0
      ? userData.spokenLanguages
      : ['English'],
  );
  const [selectedFacilityId, setSelectedFacilityId] = useState(
    userData.facilityId || '',
  );
  const [hasAttemptedSubmit, setHasAttemptedSubmit] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const resolvedTitle =
    titleSelection === OTHER_TITLE ? customTitle.trim() : titleSelection;

  const firstNameError =
    hasAttemptedSubmit && !firstName.trim() ? 'First name is required' : '';
  const lastNameError =
    hasAttemptedSubmit && !lastName.trim() ? 'Last name is required' : '';
  const facilityError =
    hasAttemptedSubmit && facilityRequired && !selectedFacilityId
      ? 'Facility is required'
      : '';
  const sortedFacilities = useMemo(
    () =>
      [...facilities].sort((left, right) =>
        (left.facility_name || left.name || '').localeCompare(
          right.facility_name || right.name || '',
        ),
      ),
    [facilities],
  );
  const selectedFacility = facilities.find(
    facility => facility.id === selectedFacilityId,
  );

  const handleSubmit = async () => {
    if (
      !firstName.trim() ||
      !lastName.trim() ||
      (facilityRequired && !selectedFacilityId)
    ) {
      setHasAttemptedSubmit(true);
      return;
    }
    setIsSaving(true);
    try {
      await onComplete(
        {
          firstName: firstName.trim(),
          lastName: lastName.trim(),
          title: resolvedTitle,
          providerPhone: combinePhoneExtension(phone.trim(), phoneExt.trim()),
          spokenLanguages,
        },
        selectedFacilityId || undefined,
      );
    } catch (error: any) {
      setIsSaving(false);
      toast({
        title: 'Could not save your profile',
        description:
          error?.message || 'Something went wrong. Please try again.',
        status: 'error',
        duration: 4000,
        isClosable: true,
      });
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={() => {}}
      isCentered
      closeOnOverlayClick={false}
      closeOnEsc={false}
      scrollBehavior="inside"
      size={{base: 'full', md: 'lg'}}
    >
      <ModalOverlay bg="blackAlpha.400" backdropFilter="blur(6px)" />
      <ModalContent
        bg={styles.surfaceBg}
        borderRadius={{base: 0, md: '2xl'}}
        mx={{base: 0, md: 4}}
        my={{base: 0, md: 'auto'}}
        overflow="hidden"
      >
        <ModalBody p={{base: 5, md: 7}}>
          <VStack align="stretch" spacing={1} mb={6}>
            <Text
              as="h1"
              color={styles.textColor}
              fontSize={{base: '2xl', md: '3xl'}}
              fontWeight="semibold"
              lineHeight="1.2"
            >
              Welcome to Opalite
            </Text>
            <Text color={styles.subtleText} fontSize={{base: 'md', md: 'lg'}}>
              Tell us a little about yourself to finish setting up your account.
            </Text>
          </VStack>

          <VStack align="stretch" spacing={5}>
            <Flex direction={{base: 'column', md: 'row'}} gap={4}>
              <FormControl isRequired isInvalid={!!firstNameError}>
                <FormLabel fontSize="sm" fontWeight="normal">
                  First name
                </FormLabel>
                <Input
                  value={firstName}
                  onChange={event => setFirstName(event.target.value)}
                  placeholder="Jane"
                  borderRadius="lg"
                  minH="44px"
                  _focusVisible={styles.inputFocusStyles}
                />
                <FormErrorMessage>{firstNameError}</FormErrorMessage>
              </FormControl>
              <FormControl isRequired isInvalid={!!lastNameError}>
                <FormLabel fontSize="sm" fontWeight="normal">
                  Last name
                </FormLabel>
                <Input
                  value={lastName}
                  onChange={event => setLastName(event.target.value)}
                  placeholder="Doe"
                  borderRadius="lg"
                  minH="44px"
                  _focusVisible={styles.inputFocusStyles}
                />
                <FormErrorMessage>{lastNameError}</FormErrorMessage>
              </FormControl>
            </Flex>

            {facilityRequired && (
              <FormControl isRequired isInvalid={!!facilityError}>
                <FormLabel fontSize="sm" fontWeight="normal">
                  Facility
                </FormLabel>
                <Menu matchWidth placement="bottom-start">
                  <MenuButton
                    as={Button}
                    variant="outline"
                    w="100%"
                    minH="54px"
                    h="auto"
                    py={2.5}
                    px={3.5}
                    borderRadius="lg"
                    borderColor={facilityError ? 'red.300' : styles.borderColor}
                    bg={styles.surfaceBg}
                    textAlign="left"
                    fontWeight="normal"
                    isDisabled={sortedFacilities.length === 0}
                    rightIcon={
                      <TablerChevronDown size={16} aria-hidden="true" />
                    }
                    _hover={{borderColor: styles.listRowSelectedBorderColor}}
                    _focusVisible={styles.focusOutlineStyles}
                  >
                    <Flex align="center" gap={3} minW={0}>
                      <Box color={styles.subtleText} flexShrink={0}>
                        <TablerBuilding size={19} aria-hidden="true" />
                      </Box>
                      <Box minW={0}>
                        <Text
                          color={
                            selectedFacility
                              ? styles.textColor
                              : styles.subtleText
                          }
                          fontSize="md"
                          lineHeight="1.25"
                          noOfLines={1}
                        >
                          {selectedFacility?.facility_name ||
                            selectedFacility?.name ||
                            (sortedFacilities.length === 0
                              ? 'No facilities available'
                              : 'Select your facility')}
                        </Text>
                        {selectedFacility &&
                          (selectedFacility.location ||
                            selectedFacility.address) && (
                            <Text
                              color={styles.subtleText}
                              fontSize="xs"
                              lineHeight="1.3"
                              mt={0.5}
                              noOfLines={1}
                            >
                              {selectedFacility.location ||
                                selectedFacility.address}
                            </Text>
                          )}
                      </Box>
                    </Flex>
                  </MenuButton>
                  <MenuList
                    zIndex="popover"
                    maxH="320px"
                    overflowY="auto"
                    p={1.5}
                    borderRadius="xl"
                    borderColor={styles.borderColor}
                    boxShadow="xl"
                    bg={styles.surfaceBg}
                  >
                    {sortedFacilities.map(facility => {
                      const isSelected = facility.id === selectedFacilityId;
                      return (
                        <MenuItem
                          key={facility.id}
                          onClick={() => setSelectedFacilityId(facility.id)}
                          borderRadius="lg"
                          px={3}
                          py={2.5}
                          bg={isSelected ? styles.listRowSelectedBg : undefined}
                          _hover={{bg: styles.hoverBg}}
                          _focus={{bg: styles.hoverBg}}
                        >
                          <Flex align="center" gap={3} w="100%" minW={0}>
                            <Box color={styles.subtleText} flexShrink={0}>
                              <TablerMapPin size={17} aria-hidden="true" />
                            </Box>
                            <Box flex="1" minW={0}>
                              <Text
                                color={styles.textColor}
                                fontSize="sm"
                                lineHeight="1.3"
                              >
                                {facility.facility_name || facility.name}
                              </Text>
                              {(facility.location || facility.address) && (
                                <Text
                                  color={styles.subtleText}
                                  fontSize="xs"
                                  lineHeight="1.35"
                                  mt={0.5}
                                >
                                  {facility.location || facility.address}
                                </Text>
                              )}
                            </Box>
                            {isSelected && (
                              <Box color="opalite.600" flexShrink={0}>
                                <TablerCheck size={17} aria-hidden="true" />
                              </Box>
                            )}
                          </Flex>
                        </MenuItem>
                      );
                    })}
                  </MenuList>
                </Menu>
                <FormErrorMessage>{facilityError}</FormErrorMessage>
                {sortedFacilities.length === 0 && (
                  <Text color="red.500" fontSize="sm" mt={2}>
                    No facilities are configured for your organization. Contact
                    your administrator to continue.
                  </Text>
                )}
              </FormControl>
            )}

            <Flex direction={{base: 'column', md: 'row'}} gap={4}>
              <FormControl>
                <FormLabel fontSize="sm" fontWeight="normal">
                  Title
                </FormLabel>
                {titleSkipped ? (
                  <Flex
                    align="center"
                    justify="space-between"
                    minH="44px"
                    px={3}
                    borderWidth="1px"
                    borderColor={styles.borderColor}
                    borderRadius="lg"
                    bg={styles.panelBg}
                  >
                    <Text fontSize="md" color={styles.subtleText}>
                      Skipped
                    </Text>
                    <Button
                      variant="link"
                      size="sm"
                      color="opalite.600"
                      fontWeight="normal"
                      onClick={() => setTitleSkipped(false)}
                    >
                      Add a title
                    </Button>
                  </Flex>
                ) : (
                  <HStack spacing={2} align="stretch">
                    <Select
                      value={titleSelection}
                      onChange={event => setTitleSelection(event.target.value)}
                      placeholder="Select title"
                      borderRadius="lg"
                      minH="44px"
                      flex="1"
                    >
                      {TITLE_OPTIONS.map(option => (
                        <option key={option} value={option}>
                          {option}
                        </option>
                      ))}
                      <option value={OTHER_TITLE}>{OTHER_TITLE}</option>
                    </Select>
                    <Button
                      variant="outline"
                      minH="44px"
                      h="44px"
                      px={5}
                      fontWeight="normal"
                      color={styles.subtleText}
                      borderColor={styles.borderColor}
                      flexShrink={0}
                      onClick={() => {
                        setTitleSkipped(true);
                        setTitleSelection('');
                        setCustomTitle('');
                      }}
                    >
                      Skip
                    </Button>
                  </HStack>
                )}
              </FormControl>
              {!titleSkipped && titleSelection === OTHER_TITLE && (
                <FormControl>
                  <FormLabel fontSize="sm" fontWeight="normal">
                    Custom title
                  </FormLabel>
                  <Input
                    value={customTitle}
                    onChange={event => setCustomTitle(event.target.value)}
                    placeholder="e.g. CNS"
                    borderRadius="lg"
                    minH="44px"
                    _focusVisible={styles.inputFocusStyles}
                  />
                </FormControl>
              )}
            </Flex>

            <FormControl>
              <FormLabel fontSize="sm" fontWeight="normal">
                Phone number
              </FormLabel>
              <HStack spacing={2} align="stretch">
                <Input
                  flex="1"
                  type="tel"
                  value={phone}
                  onChange={event => setPhone(event.target.value)}
                  placeholder="(555) 123-4567"
                  borderRadius="lg"
                  minH="44px"
                  _focusVisible={styles.inputFocusStyles}
                />
                <Input
                  w="96px"
                  flexShrink={0}
                  type="tel"
                  inputMode="numeric"
                  value={phoneExt}
                  onChange={event => setPhoneExt(event.target.value)}
                  placeholder="Ext."
                  aria-label="Phone extension"
                  borderRadius="lg"
                  minH="44px"
                  _focusVisible={styles.inputFocusStyles}
                />
              </HStack>
            </FormControl>

            <FormControl>
              <FormLabel fontSize="md" fontWeight="normal">
                Languages you speak{' '}
                <Text as="span" color={styles.subtleText}>
                  (optional)
                </Text>
              </FormLabel>
              <Text fontSize="sm" color={styles.subtleText} mb={2}>
                We use this to skip the interpreter when you already speak a
                patient&apos;s language.
              </Text>
              <LanguageMultiSelect
                value={spokenLanguages}
                onChange={setSpokenLanguages}
                ariaLabel="Search languages you speak"
              />
            </FormControl>
          </VStack>
        </ModalBody>

        <ModalFooter
          borderTopWidth="1px"
          borderColor={styles.borderColor}
          px={{base: 5, md: 7}}
        >
          <HStack w="100%" justify={{base: 'stretch', md: 'flex-end'}}>
            <Button
              variant="opalitePrimary"
              px={6}
              w={{base: '100%', md: 'auto'}}
              rightIcon={<TablerCheck size={16} />}
              onClick={handleSubmit}
              isLoading={isSaving}
              loadingText="Saving"
            >
              Finish setup
            </Button>
          </HStack>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
